import type { RecurringActivity } from '@life-rpg/shared';
import React, { useState } from 'react';

import type { RecurringActivityRepository } from './activityRepository.js';
import { titleCase, useActivityList } from './activityUi.js';
import {
  editRecurringActivityForm,
  formatRecurringActivityCadence,
  newRecurringActivityForm,
  parseRecurringActivityForm,
} from './recurringActivityForm.js';
import { RecurringActivityFields } from './RecurringActivityFields.js';

interface RecurringActivitiesProps {
  repository: RecurringActivityRepository;
  today?: () => Date;
}

const listRecurringActivities = (repository: RecurringActivityRepository) => repository.list();

export const RecurringActivities = ({
  repository,
  today = () => new Date(),
}: RecurringActivitiesProps): React.JSX.Element => {
  const { activities, setActivities, problem, setProblem } = useActivityList<
    RecurringActivity,
    RecurringActivityRepository
  >(repository, listRecurringActivities);
  const [saving, setSaving] = useState(false);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(() => newRecurringActivityForm(today()));

  const resetForm = (): void => {
    setEditingId(null);
    setForm(newRecurringActivityForm(today()));
  };

  const saveActivity = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setProblem(null);

    const parsed = parseRecurringActivityForm(form);
    if (parsed.problem !== undefined) {
      setProblem(parsed.problem);
      return;
    }

    setSaving(true);
    try {
      if (editingId === null) {
        const created = await repository.create(parsed.draft);
        setActivities((current) => [...(current ?? []), created]);
      } else {
        const updated = await repository.update(editingId, parsed.draft);
        setActivities(
          (current) =>
            current?.map((activity) => (activity.id === editingId ? updated : activity)) ?? [
              updated,
            ],
        );
      }
      resetForm();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Could not save activity.');
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (activity: RecurringActivity): void => {
    setProblem(null);
    setEditingId(activity.id);
    setForm(editRecurringActivityForm(activity));
  };

  const archiveActivity = async (id: string): Promise<void> => {
    setProblem(null);
    setArchivingId(id);
    try {
      await repository.archive(id);
      setActivities((current) => current?.filter((activity) => activity.id !== id) ?? []);
      if (editingId === id) resetForm();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Could not archive activity.');
    } finally {
      setArchivingId(null);
    }
  };

  return (
    <section aria-labelledby="recurring-activities-heading">
      <h2 id="recurring-activities-heading">Recurring activities</h2>

      <form className="activity-form" onSubmit={(event) => void saveActivity(event)}>
        <RecurringActivityFields form={form} setForm={setForm} />
        <div className="form-actions">
          <button disabled={saving} type="submit">
            {saving ? 'Saving…' : editingId === null ? 'Create activity' : 'Save changes'}
          </button>
          {editingId !== null && (
            <button className="secondary" disabled={saving} type="button" onClick={resetForm}>
              Cancel editing
            </button>
          )}
        </div>
      </form>

      {problem !== null && <p role="alert">{problem}</p>}
      {activities === null && problem === null && <p>Loading activities…</p>}
      {activities?.length === 0 && <p>No recurring activities yet.</p>}
      {activities !== null && activities.length > 0 && (
        <ul className="activity-list">
          {activities.map((activity) => (
            <li key={activity.id}>
              <article className="activity-card">
                <h3>{activity.name}</h3>
                <p>{formatRecurringActivityCadence(activity)}</p>
                <p>
                  Quick add: {activity.quickAdd.join(', ')} {activity.unitLabel}
                </p>
                <p>{titleCase(activity.difficulty)}</p>
                <div className="card-actions">
                  <button className="secondary" type="button" onClick={() => beginEdit(activity)}>
                    Edit
                  </button>
                  <button
                    className="danger"
                    disabled={archivingId === activity.id}
                    type="button"
                    onClick={() => void archiveActivity(activity.id)}
                  >
                    {archivingId === activity.id ? 'Archiving…' : 'Archive'}
                  </button>
                </div>
              </article>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
